(ns ui.paging
  (:require [geir.constants  :as    const]
            [hoplon.core     :refer [do-watch]]
            [javelin.core    :refer (cell cell=)]
            [ui.global-state :refer [ping-response]]
            [ui.util         :as    util]))

(defn- window
  "Given a window size `n`, the page number you are currently on
   (`current-page`), and the total number of pages (`total-pages`), returns a
   list of size <= `n` of page numbers that are up to (`n` / 2) pages away from
   the current page.

   `total-pages` can be `:many`, which represents the scenario where we have
   counted a limited number of pages (`geir.constants/PAGINATOR-COUNT-LIMIT`),
   and we don't know the true total because to count them would have an
   unacceptable effect on the performance of the database query. In that case,
   our window simply assumes that there is at least one extra page beyond the
   current one, so that the user can keep paging forward as long as they want.

   -----

   We used to use this back when we had a different style of pagination control
   in the UI that looked like a list of numbers, and you could click any of the
   numbers to go to that page.

   For example, the list would initially look something like this:

     1 2 3 4 5 6 7 8 9 10

   and you click any of those numbers to go to that page. As you got closer to
   page 10, the list would start to look more like this:

     4 5 6 7 8 9 10 11 12

   This function is still relevant because the `at-beginning` and `at-end` cells
   below are implemented in terms of it, and those cells inform behavior like
   whether or not the \"previous\" and \"next\" page buttons are enabled."
  [n current-page total-pages]
  (let [many-pages? (= :many total-pages)
        start       (- current-page (.ceil js/Math (/ n 2)))
        end         (+ start n)]
    (cond
      ;; If there are less pages than the size of the window, then the window is
      ;; simply all of the pages.
      (and (not many-pages?) (> n total-pages))
      (range 1 (inc total-pages))

      ;; Shift right until it starts at 1
      (<= start 0)
      (range 1 (inc n))

      ;; Shift left until end = total-pages
      (and (not many-pages?) (> end total-pages))
      (range (inc (- total-pages n))
             (inc total-pages))

      :else
      (range start end))))

(defn refresh!
  [{:keys [params]}]
  (swap! params update :refresh inc))

(defn clear!
  [{:keys [data] :as this}]
  (reset! data nil)
  this)

(defn activate!
  [{:keys [params] :as this}]
  (swap! params assoc :active true)
  this)

(defn deactivate!
  [{:keys [params] :as this}]
  (clear! this)
  (swap! params assoc :active false))

(defn params->page-opts
  [{:keys [page-size current-page] :as params}]
  (merge (select-keys params [:filters :sorting])
         {:paging {:size page-size
                   :from (* (dec current-page) page-size)}}))

(defn page-opts
  [{:keys [params]}]
  (params->page-opts (util/safe-deref params)))

(defn fetch!
  [remote data fetch-state errors params]
  (when remote
    (let [^js prom (remote (params->page-opts params))]
      (swap! fetch-state conj prom)
      (doto prom
        (.done #(do (reset! data %)
                    (reset! errors [])
                    (swap! fetch-state disj prom)))
        (.fail #(do (swap! errors conj %)
                    (swap! fetch-state disj prom)))))))

(defn set-page-size!
  [{:keys [current-page page-size] :as this} new-size]
  (reset! current-page 1)
  (reset! page-size new-size)
  this)

(defn next!
  [{:keys [current-page] :as this}]
  (swap! current-page inc)
  this)

(defn prev!
  [{:keys [current-page] :as this}]
  (swap! current-page dec)
  this)

(defn set-page!
  [{:keys [current-page] :as this} n]
  (reset! current-page n)
  this)

(defn filter-equals!
  [{:keys [filters] :as this} & kv-pairs]
  (->> kv-pairs
       (partition-all 2)
       (reduce (fn [r [k v]]
                 (assoc r k (when v {:type := :val v})))
               {})
       (swap! filters merge))
  this)

(defn filter-equals?
  [{:keys [filters]} k v]
  (= (k @filters)
     {:type := :val v}))

(defn set-filters!
  [{:keys [filters] :as this} filter-spec]
  (reset! filters filter-spec)
  this)

(defn set-sorting!
  [{:keys [sorting] :as this} sorting-spec]
  (reset! sorting sorting-spec)
  this)

(defn sort!
  ([this column]
   (sort! this column nil))
  ([{:keys [sorting] :as this} column opts]
   (let [{:keys [by order]} @sorting]
     (reset! sorting
             {:by    column
              :order (if (not= by column)
                       ;; If we aren't already sorting by this column, then sort
                       ;; by that column by its default :order if it has one, or
                       ;; ascending by default.
                       (or (:order opts) :asc)
                       ;; If we're already sorting by this column, then reverse
                       ;; the current order.
                       (get {:asc :desc :desc :asc} order))})
     this)))

(defn load-entity!
  "Loads a single entity into the page."
  [this entity-key entity]
  (doto this
    ;; Clear out existing data.
    clear!
    ;; Set the filter to one that will load a specific entity.
    (filter-equals! entity-key entity)
    ;; Mark as active so that it will fetch upon refresh.
    activate!
    ;; Refresh (this should cause a fetch).
    refresh!))

(defn watch-map
  "When cell `c` changes, update the filter of pager `this` according to `mapping`.

  Example: (watch-map rpc/creatives {:flightid :id} flight)
  When flight changes, rpc/creatives will look something like

  {:filters {:flightid {:type := :val 123}}}

  where `123` is (:id flight)"
  [this mapping c]

  (let [source-keys (vals mapping)
        watch-map   (cell= (and c (select-keys c source-keys)))]
    (cell= (when watch-map
             (clear! this)
             (apply filter-equals!
                    this
                    (mapcat (fn [[filter-attr cell-attr]]
                              [filter-attr (get watch-map cell-attr)])
                            mapping))
             (activate! this)))))

(defn watch-val
  [this k c]
  (util/on-change c (fn [v] (when (and v (not (filter-equals? this k v)))
                             (clear! this)
                             (filter-equals! this k v)))))

(defn paginate
  "Returns a map representing a \"paged remote\" or \"pager.\"

   The values of the map are cells that are updated as the user changes pages,
   page sizes, filters, etc. in the UI.

   When `storage` (an atom) is supplied, the page-size is stored and updated in
   the atom."
  [remote & {:keys [filters sorting page-size storage initial]}]
  (let [fetch-state  (cell #{})
        errors       (cell [])
        counter      (cell -1)
        params       (cell
                       (merge {:page-size    (or page-size 25)
                               :current-page 1
                               :filters      (or filters {})
                               :sorting      (or sorting
                                                 {:by :id :order :desc})
                               :refresh      0
                               :active       false}
                              (when storage
                                (select-keys @storage [:page-size]))))
        current-page (util/path-cell params [:current-page])
        page-size    (util/path-cell params [:page-size])

        things       (or initial (cell nil))

        rows         (cell= (map :__row__ things))
        total-rows   (cell= (:__rows__ (first things)))
        many-rows?   (cell= (= total-rows const/PAGINATOR-COUNT-LIMIT))
        pages        (cell= (.ceil js/Math (/ total-rows page-size)))
        page-window  (cell= (window 10 current-page (if many-rows?
                                                      :many
                                                      pages)))
        at-beginning (cell= (or (zero? pages)
                                (= current-page (first page-window))))
        ;; HACK: If there are slightly more rows than we can count, but a small
        ;; enough number of rows that the user could feasibly set the page size
        ;; to 250 and page forward until they reach the limit, then the
        ;; experience is suboptimal once they reach the end of the results. We
        ;; have to know whether there are more rows to load or not, so that we
        ;; can determine whether or not we are at the end and disable the "next
        ;; page" button.
        ;;
        ;; We're covering 99% of these edge cases here by concluding that we are
        ;; on the last page if the number of rows on the page is less than the
        ;; page size.
        ;;
        ;; There is still a particularly edgy edge case where the total number
        ;; of items is a multiple of the page size, such that the last page has
        ;; exactly the page size number of items, yet there are no rows to load.
        ;; In this super edge case, we are in fact at the end, but we don't
        ;; realize it because there are `page-size` rows in the current page and
        ;; it looks like there might be more to load. This rare edge case is not
        ;; addressed here, and will result in unspecified behavior.
        ;;
        ;; TODO: If/when we want to address the unspecified behavior, there are
        ;; a few options we could consider, including:
        ;;
        ;; * Making an additional request to see if there are any additional
        ;;   rows to load before we load them.
        ;; * Add special logic on the frontend that says "if I'm loading the
        ;;   next page, but there are no more rows, and it's a 'many pages'
        ;;   situation, then don't load the next page, just stay on the current
        ;;   one"
        ;; * Maybe this will be resolved automatically by the migration to
        ;;   TypeScript.
        ;;
        ;; See discussion in Shortcut story:
        ;; https://app.shortcut.com/adzerk/story/38587/sites-paging-is-broken
        at-end       (cell= (if many-rows?
                              (< (count things) page-size)
                              (or (zero? pages)
                                  (= current-page (last page-window)))))]
    (do-watch (cell= (-> ping-response :user :networkid))
              (fn [o n]
                (when (and o n (not (util/str= o n)) (:active @params))
                  (fetch! remote things fetch-state errors @params))))
    (util/on-change params
      (fn [{:keys [active] :as new-params}]
        (when-not (and initial (>= 0 @counter))
          (when active (fetch! remote things fetch-state errors new-params))
          (when storage (reset! storage (select-keys new-params [:page-size]))))
        (swap! counter inc)))
    {:data         things
     :remote       remote
     :fetching     (cell= (seq fetch-state))
     :errors       errors

     :params       params
     :filters      (util/path-cell params [:filters])
     :sorting      (util/path-cell params [:sorting])
     :current-page current-page
     :page-size    page-size
     :selected     (cell #{})

     :rows         rows
     :total-rows   total-rows
     :many-rows?   many-rows?
     :pages        pages
     :at-beginning at-beginning
     :at-end       at-end}))
